import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Nav bell icon for logged-in users. Shows unread chat mentions count.
 * Click → /community (jumps to channel with most unread).
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const loc = useLocation();
  const [unread, setUnread] = useState({ total: 0, channels: {} });
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied",
  );

  useEffect(() => {
    if (!user) return;
    const fetch = () => api.get("/chat/notifications/unread").then((r) => setUnread(r.data)).catch(() => {});
    fetch();
    const id = setInterval(fetch, 15000);
    return () => clearInterval(id);
  }, [user, loc.pathname]);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      const p = await Notification.requestPermission();
      setPermission(p);
    } else {
      setPermission(Notification.permission);
    }
  };

  if (!user) return null;

  // Channel with the most unread = jump target
  let target = "/community";
  const ch = unread.channels || {};
  const top = Object.entries(ch).sort((a, b) => b[1] - a[1])[0];
  if (top && top[1] > 0) target = `/community?channel=${top[0]}`;

  return (
    <Link
      to={target}
      onClick={requestPermission}
      data-testid="notification-bell"
      title={unread.total > 0 ? `${unread.total} nových správ` : "Notifikácie"}
      className="relative inline-flex items-center justify-center w-9 h-9 border border-zinc-700 hover:border-gold-600 transition-colors"
    >
      <Bell className={`w-4 h-4 ${unread.total > 0 ? "text-gold-400" : "text-zinc-400"}`} />
      {unread.total > 0 && (
        <span data-testid="notification-bell-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-gold-500 text-black text-[10px] font-bold rounded-full flex items-center justify-center">
          {unread.total > 99 ? "99+" : unread.total}
        </span>
      )}
      {permission === "default" && (
        <span className="absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
      )}
    </Link>
  );
}
